/* Release gate (releases DEV-PLAN Phase 1, "Resolver rule").
 *
 * Reads the data-release-* attributes printed by snippets/release-state.liquid
 * and answers, per product, whether the shopper may buy right now:
 *
 *   window.releaseGate = {phase, canBuy(), reason(), ...}
 *
 * for the product page's own product (the element with
 * data-release-scope="pdp"). The product page script stays the only writer of
 * the PDP buttons (disabled, id, data-bis-id, add-to-cart-btn, label); this file
 * only answers canBuy()/reason() for it and announces boundary crossings with a
 * `release-state:changed` event on document.
 *
 * On surfaces without the PDP script (product cards, the bundle page) this file
 * toggles `hidden` between the existing button ([data-release-default]) and a
 * PRO / Coming Soon label ([data-release-label]); nothing else.
 *
 * No release state on the page: phase is null, canBuy() is true, reason() is
 * 'stock', nothing is fetched and nothing is touched. Any doubt fails toward
 * public (business rule 5).
 *
 * Must load before the PDP inline script runs (layout/theme.liquid, defer). */
(() => {
  'use strict';

  const RANK = {coming_soon: 0, pro: 1, public: 2};
  const STATES = new Set(['scheduled', 'pro', 'public', 'done']);
  const SHORT_PRO_LABEL = 'PRO EARLY ACCESS';
  const MAX_TIMER_MS = 60 * 60 * 1000; // re-check at least hourly (setTimeout caps near 24.8 days)
  const BOUNDARY_SLACK_MS = 250;

  let offsetMs = 0;          // server time minus local time, from the /cart.js Date header
  let clockSettled = false;  // true once the offset is known (or could not be)
  let clockRequested = false;
  let timer = null;
  const floors = new WeakMap();    // element -> highest phase rank seen once the clock settled
  const lastPhase = new WeakMap(); // element -> phase at the last announcement

  function now() { return Date.now() + offsetMs; }

  // Usable only as an ISO-8601 date-time string (YYYY-MM-DDThh...), the same
  // rule as the snippet and the worker; anything else is NaN (unusable).
  const ISO_TIME = /^\d{4}-\d{2}-\d{2}T/;
  function parseTime(value) {
    if (typeof value !== 'string' || !ISO_TIME.test(value)) return NaN;
    return Date.parse(value);
  }

  function gateElements(root = document) {
    const found = [];
    if (root.nodeType === 1 && root.matches('[data-release-gate]')) found.push(root);
    if (root.querySelectorAll) found.push(...root.querySelectorAll('[data-release-gate]'));
    return found;
  }

  function primaryElement() {
    return document.querySelector('[data-release-gate][data-release-scope="pdp"]');
  }

  function read(el) {
    if (!el) return null;
    const state = String(el.dataset.releaseState || '').trim().toLowerCase();
    if (!STATES.has(state)) return null;
    const proOpens = parseTime(el.dataset.releaseProOpens);
    return {
      state,
      proOpens,
      publicOpens: parseTime(el.dataset.releasePublicOpens),
      // PRO-only release (2026-10-22): the snippet prints data-release-pro-only
      // only for the strict marker; it also needs a usable T1 and NO public time.
      proOnly: el.dataset.releaseProOnly === 'true' && Number.isFinite(proOpens) && !el.dataset.releasePublicOpens,
      isPro: el.dataset.releaseIsPro === 'true',
      proNotify: el.dataset.releaseProNotify === 'true',
      proLabel: el.dataset.releaseProLabel || SHORT_PRO_LABEL
    };
  }

  // The raw phase for a release at time `at`; null means "not a release".
  // The ONE phase contract, shared with the Liquid snippet, the Velocity worker
  // and Back in Stock (tests/release-state/phase-vectors.json).
  // Unusable accepted times (missing, invalid, T1 > T2) fail per state, as the
  // worker's rules do: scheduled stays Coming Soon (nobody buys before PRO
  // opens); pro ends the PRO window early (public); public/done stay public.
  function computePhase(info, at) {
    if (!info) return null;
    if (info.state === 'public' || info.state === 'done') return 'public';
    // PRO-only: Coming Soon until T1, then PRO with no end (no T2, no fail-open).
    if (info.proOnly) return info.state === 'pro' || at >= info.proOpens ? 'pro' : 'coming_soon';
    const t1 = info.proOpens;
    const t2 = info.publicOpens;
    if (info.state === 'pro') return Number.isFinite(t2) && at < t2 ? 'pro' : 'public';
    // scheduled
    if (!Number.isFinite(t1) || !Number.isFinite(t2) || t1 > t2) return 'coming_soon';
    if (at < t1) return 'coming_soon';
    return at < t2 ? 'pro' : 'public';
  }

  // Phase with floors: once this page has seen a later phase on the server
  // clock it never goes back (a later clock correction or a sleeping laptop
  // cannot re-gate a product that already opened).
  function phaseOf(el) {
    const info = read(el);
    let phase = computePhase(info, now());
    if (phase === null) return null;
    const floor = floors.get(el);
    if (floor !== undefined && RANK[phase] < floor) {
      phase = Object.keys(RANK).find(key => RANK[key] === floor);
    }
    if (clockSettled) floors.set(el, Math.max(floor ?? 0, RANK[phase]));
    return phase;
  }

  function evaluate(el) {
    const info = read(el);
    const phase = info ? phaseOf(el) : null;
    let reason = 'stock';
    if (phase === 'coming_soon') reason = 'release_coming_soon';
    else if (phase === 'pro' && !info.isPro) reason = 'release_pro';
    return {phase, canBuy: reason === 'stock', reason};
  }

  // Non-PDP surfaces: toggle `hidden` only.
  function applySurface(el) {
    if (el.dataset.releaseScope === 'pdp' || !read(el)) return;
    const {reason} = evaluate(el);
    el.querySelectorAll('[data-release-default]').forEach(node => { node.hidden = reason !== 'stock'; });
    el.querySelectorAll('[data-release-label]').forEach(node => {
      const kind = node.dataset.releaseLabel;
      node.hidden = !((kind === 'coming_soon' && reason === 'release_coming_soon') || (kind === 'pro' && reason === 'release_pro'));
    });
  }

  function gatedElements() {
    return gateElements().filter(el => read(el));
  }

  // Next T1/T2 strictly in the future across every gated element on the page.
  // A PRO-only release has no T2 (no public timer): only its T1 while
  // scheduled; once in pro nothing (Velocity opens it, a reload shows it).
  function nextBoundary() {
    const at = now();
    let next = Infinity;
    for (const el of gatedElements()) {
      const info = read(el);
      if (info.state === 'public' || info.state === 'done') continue;
      if (info.proOnly && info.state === 'pro') continue;
      for (const t of [info.proOpens, info.publicOpens]) {
        if (Number.isFinite(t) && t > at && t < next) next = t;
      }
    }
    return next;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = null;
    const next = nextBoundary();
    if (next === Infinity) return;
    const delay = Math.min(Math.max(next - now() + BOUNDARY_SLACK_MS, 0), MAX_TIMER_MS);
    timer = setTimeout(tick, delay);
  }

  // Re-evaluates every gated element; repaints surfaces and announces a change
  // when any element's phase moved.
  function tick() {
    let changed = false;
    for (const el of gatedElements()) {
      const phase = phaseOf(el);
      if (lastPhase.has(el) && lastPhase.get(el) !== phase) changed = true;
      lastPhase.set(el, phase);
      applySurface(el);
    }
    if (changed) {
      const primary = primaryElement();
      document.dispatchEvent(new CustomEvent('release-state:changed', {
        detail: {phase: primary ? evaluate(primary).phase : null}
      }));
    }
    schedule();
  }

  function needsClock() {
    return gatedElements().some(el => {
      const state = read(el).state;
      return state === 'scheduled' || state === 'pro';
    });
  }

  // Server-corrected clock, once per page, from the Date header of /cart.js.
  function syncClock() {
    if (clockRequested || !needsClock()) return;
    clockRequested = true;
    const sent = Date.now();
    fetch('/cart.js', {cache: 'no-store', credentials: 'same-origin', headers: {Accept: 'application/json'}})
      .then(response => {
        const received = Date.now();
        const server = Date.parse(response.headers.get('date') || '');
        // The header has whole seconds; +500 ms centres the truncation error.
        if (Number.isFinite(server)) offsetMs = server + 500 - (sent + received) / 2;
      })
      .catch(() => { /* keep the local clock */ })
      .finally(() => { clockSettled = true; tick(); });
  }

  function start(root) {
    const found = gateElements(root);
    if (!found.length) return;
    found.forEach(el => {
      if (!read(el)) return;
      lastPhase.set(el, phaseOf(el));
      applySurface(el);
    });
    syncClock();
    schedule();
  }

  window.releaseGate = {
    get phase() { return evaluate(primaryElement()).phase; },
    canBuy() { return evaluate(primaryElement()).canBuy; },
    reason() { return evaluate(primaryElement()).reason; },
    // Notify Me in the PRO window for non-PRO shoppers (snippet flag).
    proNotify() { return read(primaryElement())?.proNotify === true; },
    proLabel(short) {
      const info = read(primaryElement());
      return short || !info ? SHORT_PRO_LABEL : info.proLabel;
    },
    evaluate,
    now,
    refresh: tick
  };

  start(document);

  // Cards rendered later (recommendation carousels fetch their HTML).
  if ('MutationObserver' in window) {
    new MutationObserver(records => {
      for (const record of records) {
        record.addedNodes.forEach(node => { if (node.nodeType === 1) start(node); });
      }
    }).observe(document.documentElement, {childList: true, subtree: true});
  }

  // Timers are throttled in background tabs; catch up when the tab returns.
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') tick(); });
  window.addEventListener('pageshow', event => { if (event.persisted) tick(); });
})();
